import "./Admin-global-search.css";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { clearSession } from "../../utils/session";

const CATEGORIES = [
  { value: "all", label: "All" },
  { value: "orders", label: "Orders" },
  { value: "sourcing_requests", label: "Sourcing Requests" },
  { value: "restock_requests", label: "Restock Requests" },
  { value: "products", label: "Products" },
  { value: "clients", label: "End-Customers" },
];

const SECTION_LABELS = {
  orders: "Orders",
  sourcing_requests: "Sourcing Requests",
  restock_requests: "Restock Requests",
  products: "Products",
  clients: "End-Customers",
};

const SECTION_ORDER = ["orders", "sourcing_requests", "restock_requests", "products", "clients"];

const EMPTY_RESULTS = { orders: [], sourcing_requests: [], restock_requests: [], products: [], clients: [] };

function AdminGlobalSearch() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState("all");
  const [searchBy, setSearchBy] = useState("keyword");
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  // The search could not be run (not "nothing matched").
  const [failed, setFailed] = useState(false);
  // Which search the answer on screen belongs to. An answer for words since
  // changed is dropped, so a slow search never replaces a newer one's results.
  const seqRef = useRef(0);

  const clearKeyword = (e) => {
    if (e) e.stopPropagation();
    setKeyword("");
    setResults(EMPTY_RESULTS);
    setHasSearched(false);
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const seq = ++seqRef.current;
    if (!keyword.trim()) {
      setResults(EMPTY_RESULTS);
      setHasSearched(false);
      setIsSearching(false);
      setFailed(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const token = localStorage.getItem("token");
      if (!token) return;

      setIsSearching(true);
      setFailed(false);
      const fData = new FormData();
      fData.append("token", token);
      fData.append("keyword", keyword.trim());
      fData.append("search_by", searchBy);
      fData.append("category", category);

      axios
        .post(`${process.env.REACT_APP_BASE_API_URL}/admin/global_search`, fData)
        .then((res) => {
          if (seq !== seqRef.current) return;
          if (res.data?.type === "success") {
            setResults({ ...EMPTY_RESULTS, ...res.data.payload });
          } else {
            setResults(EMPTY_RESULTS);
            setFailed(true);
          }
        })
        .catch((error) => {
          if (seq !== seqRef.current) return;
          if (error.response?.status === 401) {
            clearSession();
            navigate("/");
            return;
          }
          setResults(EMPTY_RESULTS);
          setFailed(true);
        })
        .finally(() => {
          if (seq !== seqRef.current) return;
          setIsSearching(false);
          setHasSearched(true);
        });
    }, 400);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, category, searchBy, isOpen]);

  const goToResult = (link) => {
    setIsOpen(false);
    setKeyword("");
    navigate(link);
  };

  const totalResults = SECTION_ORDER.reduce((sum, key) => sum + (results[key]?.length || 0), 0);
  const sectionsToShow = category === "all" ? SECTION_ORDER : [category];

  return (
    <div className="ags-container" ref={containerRef}>
      <div className={`ags-bar ${isOpen ? "is-active" : ""}`} onClick={() => setIsOpen(true)}>
        <i className="fas fa-search ags-bar-icon"></i>
        <input
          type="text"
          className="ags-bar-input"
          placeholder="Search orders, requests, products, end-customers..."
          value={keyword}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => setKeyword(e.target.value)}
        />
        {keyword && (
          <button type="button" className="ags-bar-clear" onClick={clearKeyword} title="Clear">
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>

      {isOpen && (
        <div className="ags-panel">
          <div className="ags-controls">
            <select className="ags-select" value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <select className="ags-select" value={searchBy} onChange={(e) => setSearchBy(e.target.value)}>
              <option value="keyword">By Keyword</option>
              <option value="id">By ID</option>
            </select>
          </div>

          <div className="ags-results">
            {isSearching && <div className="ags-status">Searching...</div>}

            {!isSearching && failed && (
              <div className="ags-status" role="alert">Search isn't available right now. Try again.</div>
            )}

            {!isSearching && !failed && hasSearched && totalResults === 0 && (
              <div className="ags-status">No results found</div>
            )}

            {!isSearching &&
              sectionsToShow.map((sectionKey) => {
                const items = results[sectionKey] || [];
                if (items.length === 0) return null;
                return (
                  <div className="ags-section" key={sectionKey}>
                    <div className="ags-section-title">{SECTION_LABELS[sectionKey]}</div>
                    {items.map((item) => (
                      <button
                        type="button"
                        key={`${sectionKey}-${item.id}`}
                        className="ags-result"
                        onClick={() => goToResult(item.link)}
                      >
                        <span className="ags-result-thumb">
                          {item.image ? (
                            <img src={item.image} alt="" onError={(e) => { e.target.onerror = null; e.target.style.display = "none"; }} />
                          ) : (
                            <span className="ags-result-thumb-fallback">
                              {(item.title || "?").charAt(0).toUpperCase()}
                            </span>
                          )}
                        </span>
                        <span className="ags-result-text">
                          <span className="ags-result-title-row">
                            <span className="ags-result-title">{item.title}</span>
                            {item.extra_count > 0 && (
                              <span className="ags-result-badge">+{item.extra_count}</span>
                            )}
                          </span>
                          <span className="ags-result-subtitle">{item.subtitle}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminGlobalSearch;
